import React, { useContext, useRef, useState } from 'react';
import {
  arrayOf,
  bool,
  extend,
  params,
  QueryContext,
  shape,
  string,
  number,
  useDataModel,
  QueryProvider
} from '@thd-nucleus/data-sources';
import { node } from 'prop-types';
import {
  Col, Row, Accordion, AccordionBody, AccordionTitle, Placeholder
} from '@thd-olt-component-react/core-ui';
import { ExperienceContext, useStoreId, useConfigService } from '@thd-nucleus/experience-context';
import { PaintPIPRedirector, DynamicComponent, AppContext } from '@thd-nucleus/app-render';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import { HdhomeSignup } from '@thd-olt-component-react/email-signup';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { MediaGalleryWithHotspotsDesktop } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { FeatureBasedRecommendationsImporter } from '@thd-olt-component-react/feature-based-recommendations';
import { Price } from '@thd-olt-component-react/price';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { Promotion } from '@thd-olt-component-react/promotion';
import { PromotionProductsAnchor } from '@thd-olt-component-react/promotion-products';
import {
  ProductOverviewDesktop,
  ProductOverviewAccordionTitle
} from '@thd-olt-component-react/product-overview';
import {
  QuestionsAndAnswersDesktop,
  QuestionsAndAnswersAccordionTitle
} from '@thd-olt-component-react/questions-and-answers';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { useLocation, useNavigate, useParams } from '@thd-olt-component-react/router';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { SpecificationsAccordionTitle } from '@thd-olt-component-react/specifications';
import { StickyNav } from '@thd-olt-component-react/sticky-nav';
import {
  RatingsAndReviewsDesktopImporter,
  RatingsAndReviewsAccordionTitle
} from '@thd-olt-component-react/ratings-and-reviews';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import {
  RecentlyViewedWrapper,
  SupplementalRecommendations
} from '@thd-olt-component-react/thd-recs-containers';
import { SponsoredPipBanner } from '@thd-olt-component-react/sponsored-content';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { Link } from '@one-thd/sui-atomic-components';
import {
  QuestionsAndAnswersAccordionBody, QuestionsAndAnswersAccordionBodyLoad,
  RatingsAndReviewsAccordionBody, RatingsAndReviewsAccordionBodyLoad,
  ProductOverviewAccordionBody, ProductOverviewAccordionBodyLoad,
  SpecificationsAccordionBody, SpecificationsAccordionBodyLoad
} from './dynamic-imports';
import {
  pipCampaignId, requestKey, subscriptionType
} from '../../constants';
import { PaintReorderBuyBox } from './PaintReorderBuyBox';
import '../../styles/product-page.scss';

const ZoneCard = ({ children }) => {
  return <div className="zone-card">{children}</div>;
};

ZoneCard.propTypes = {
  children: node.isRequired
};

export const PaintReorder = () => {
  declareContexts([QueryContext, ExperienceContext, CustomerContext]);
  const HYDRATION_STANDARD_SCROLL_BUFFER = 750;
  const STICKY_HEADER_HEIGHT = 115;
  const qctx = useContext(QueryContext);
  const { defaultVariables } = useContext(QueryContext) || {};
  const { instance } = useContext(AppContext);
  const thdSeoBotDetected = instance?.thdSEOBotDetection;
  const arRef = useRef(null);
  const { itemId, configId } = useParams();
  const storeId = useStoreId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const marqueeRef = React.createRef();
  const marqueeRowRef = React.createRef();
  let fsSupplementalRecs = useConfigService('fs:showSupplementalRecs');
  let fsPaintBodfsEnabled = useConfigService('fs:paintBodfsEnabled');
  let fsPaintSthEnabled = useConfigService('fs:paintSthEnabled');
  let fsPaintBossEnabled = useConfigService('fs:paintBossEnabled');
  const thdCustomer = useThdCustomer();

  const stickyNavRef = useRef(null);
  const productOverviewRef = useRef(null);
  const specificationsRef = useRef(null);
  const questionsRef = useRef(null);
  const ratingsRef = useRef(null);
  const breadCrumbsLabels = [
    { label: 'Account', url: '/account/view/landingpage' },
    { label: 'Paint Purchase History', url: '/myaccount/paintPurchaseHistory' },
    { label: 'Reorder Color', url: '' }
  ];

  const handleARClick = () => {
    arRef.current.click();
  };

  const onChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      navigate(canonicalUrl, { state: { disableScroll: true } });
    }
  };

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId,
      configId,
      skipPaintDetails: !configId
    },
    skip: !itemId,
    ssr: false
  });
  const { brandId } = productData?.product?.paintDetails || {};
  const { upc: upcCode } = productData?.product?.identifiers || {};
  const [expanded, setExpanded] = useState('');

  const handleAccordionChange = (accordion, canCloseAccordion = true) => {
    if (!expanded[accordion] || canCloseAccordion) {
      setExpanded((prevState) => ({ ...prevState, [accordion]: !(prevState[accordion]) }));
    }
  };

  const scrollTo = (offset, callback) => {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener('scroll', onScroll);
        callback();
      }
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    });
  };

  const handleProductDetailsClick = (accordion) => {
    handleAccordionChange(accordion, false);
  };

  const handleViewMoreDetails = () => {
    const offset = document.getElementById('product-section-overview').offsetTop - STICKY_HEADER_HEIGHT;
    scrollTo(offset, () => handleAccordionChange('#product-section-overview', false));
  };

  return (
    <>
      <ErrorBoundary name="redirector">
        <PaintPIPRedirector
          configId={configId}
        />
      </ErrorBoundary>
      <ErrorBoundary name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </ErrorBoundary>
      <ErrorBoundary name="pip-content">
        <Row className="isBound pip-page">
          <Row>
            <ErrorBoundary name="stickynav">
              <StickyNav
                itemId={itemId}
                configId={configId}
                ref={stickyNavRef}
                ratingsRef={ratingsRef}
                onRatingsClick={() => handleAccordionChange('#product-section-rr', false)}
              >
                <StickyNav.Links>
                  <StickyNav.Link targetRef={productOverviewRef}>
                    <div
                      className="navlink-pso"
                      onClick={() => handleAccordionChange('#product-section-overview', false)}
                      role="button"
                      tabIndex={0}
                    >Product Details
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={specificationsRef}>
                    <div
                      className="navlink-specs"
                      onClick={() => handleAccordionChange('#product-section-key-feat', false)}
                      role="button"
                      tabIndex={0}
                    >Specifications
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={questionsRef}>
                    <div
                      className="navlink-qa"
                      onClick={() => handleAccordionChange('#product-section-qa', false)}
                      role="button"
                      tabIndex={0}
                    >Questions & Answers
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={ratingsRef}>
                    <div
                      className="navlink-rr"
                      onClick={() => handleAccordionChange('#product-section-rr', false)}
                      role="button"
                      tabIndex={0}
                    >Customer Reviews
                    </div>
                  </StickyNav.Link>
                </StickyNav.Links>
              </StickyNav>
            </ErrorBoundary>
          </Row>
          <Row className="u--paddingTop u--paddingBottom">
            <ErrorBoundary name="breadcrumb-sharebelt-grp">
              <Col
                className="u__visible"
                flatten
                sm="7"
              >
                <ErrorBoundary name="breadcrumbs">
                  <Breadcrumbs staticCrumbs={breadCrumbsLabels} hideOnNoCategory={false} />
                </ErrorBoundary>
              </Col>
            </ErrorBoundary>
          </Row>
          <Row>
            <ErrorBoundary name="zone-a">
              <Col nopadding>
                <Row forwardedRef={marqueeRowRef}>
                  <Col>
                    <Discontinued itemId={itemId} />
                  </Col>
                  <Col
                    className="sticky"
                    flatten
                    sm="7"
                  >
                    <Col
                      className="zone-card overflow"
                      sm="12"
                    >
                      <div ref={marqueeRef}>

                        <Col sm="12">
                          <ErrorBoundary name="product-details">
                            <ProductDetails itemId={itemId} configId={configId}>
                              <ProductDetails.Badge itemId={itemId} configId={configId} />
                              <ProductDetails.BrandCollection showBrandHyperLink />
                              <div className="product-details__badge-title--wrapper">
                                <ProductDetails.Title />
                              </div>
                              <div className="product-details__favorites">
                                <Sharebelt itemId={itemId}>
                                  <ShareFavorite
                                    showCircle
                                    showFavoritesCount
                                  />
                                </Sharebelt>
                              </div>
                              <ProductDetails.Ratings
                                offsetselector="#sticky-nav"
                                qaselector="#product-section-qa"
                                showBadge
                                target="#product-section-rr"
                                onClick={handleProductDetailsClick}
                              />
                            </ProductDetails>
                          </ErrorBoundary>
                        </Col>

                        <Col>
                          <ErrorBoundary name="media-gallery-desktop">
                            <MediaGalleryWithHotspotsDesktop
                              highRes
                              hover
                              itemId={itemId}
                              marqueeRef={marqueeRef}
                              marqueeRowRef={marqueeRowRef}
                              onMediaClick={handleARClick}
                              showLoader
                              configId={configId}
                            />
                          </ErrorBoundary>
                        </Col>

                        <Col>
                          <ErrorBoundary name="sharebelt">
                            <Sharebelt
                              horizontal
                              itemId={itemId}
                            >
                              <ShareBeltShareButton email />
                              <ShareBeltPrint />
                            </Sharebelt>
                          </ErrorBoundary>
                        </Col>

                      </div>
                    </Col>
                  </Col>
                  <Col
                    flatten
                    sm="5"
                  >
                    <div className="overflow">
                      <Row>
                        <Col className="zone-card overflow">
                          <ErrorBoundary name="price">
                            <Price
                              channel="desktop"
                              disableRangePricing
                              displayEachUom={false}
                              itemId={itemId}
                              large
                              clsRemediation={{
                                placeholders: true,
                                preservePlaceholders: true
                              }}
                            />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card overflow promotion__card">
                          <ErrorBoundary name="promotion">
                            <Promotion
                              hideConditionalPromoDescription
                              itemId={itemId}
                            />
                          </ErrorBoundary>
                        </Col>
                        <Col className="zone-card overflow consumer-credit-messaging__card">
                          <ErrorBoundary name="consumer-card-messaging">
                            <ConsumerCardMessaging
                              itemId={itemId}
                              clsRemediation={{
                                placeholders: true,
                                preservePlaceholders: true
                              }}
                              isFinancePromoEligible
                            />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card overflow">
                          <ErrorBoundary name="promotion-products">
                            <PromotionProductsAnchor
                              itemId={itemId}
                            />
                          </ErrorBoundary>
                        </Col>
                        <Col className="zone-card">
                          <ErrorBoundary name="salient">
                            <SalientPoints
                              itemId={itemId}
                              max={3}
                            >
                              <SalientListItem>
                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                <Link
                                  underline="always"
                                  onClick={handleViewMoreDetails}
                                >
                                  View More Details
                                </Link>
                              </SalientListItem>
                            </SalientPoints>
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card overflow">
                          <ErrorBoundary name="aisle-bay">
                            <AisleBay
                              itemId={itemId}
                            />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card overflow">
                          <ErrorBoundary name="super-sku">
                            <SuperSku
                              itemId={itemId}
                              configId={configId}
                              onChange={onChange}
                              large
                              centeredCards
                              disableMediaAndAvailability
                              isPaintReorder
                            />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card">
                          <ErrorBoundary name="order-sample overflow">
                            <OrderSample itemId={itemId}>
                              Need a closer look?
                            </OrderSample>
                          </ErrorBoundary>
                        </Col>

                        <Col flatten>
                          <ErrorBoundary name="buybox">
                            {
                              storeId !== '8119'
                              && (
                                <PaintReorderBuyBox
                                  itemId={itemId}
                                  isB2BCustomer={thdCustomer?.isB2BCustomer}
                                  configId={configId}
                                  brandId={brandId}
                                  upcCode={upcCode}
                                  storeId={storeId}
                                  disableBODFS={!fsPaintBodfsEnabled}
                                  disableSTH={!fsPaintSthEnabled}
                                  disableBOSS={!fsPaintBossEnabled}
                                  sticky
                                />
                              )
                            }

                          </ErrorBoundary>
                        </Col>
                        <Col className="zone-card overflow">
                          <ErrorBoundary name="gcc">
                            <GlobalCustomConfigurator itemId={itemId} />
                          </ErrorBoundary>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Col>
            </ErrorBoundary>
          </Row>

          <ErrorBoundary name="collapsible-sections">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="collapsible-sections-accordion"
                className="grid"
                placeholder={(
                  <>
                    <div
                      ref={productOverviewRef}
                      id="product-section-overview"
                      className="accordion-placeholder"
                    >
                      <Placeholder
                        width="100%"
                        height="82px"
                        color="#F5F5F5"
                      />
                    </div>
                    <div
                      ref={specificationsRef}
                      id="product-section-key-feat"
                      className="accordion-placeholder"
                    >
                      <Placeholder
                        width="100%"
                        height="82px"
                        color="#F5F5F5"
                      />
                    </div>
                    <div
                      ref={questionsRef}
                      id="product-section-qa"
                      className="accordion-placeholder"
                    >
                      <Placeholder
                        width="100%"
                        height="82px"
                        color="#F5F5F5"
                      />
                    </div>
                    <div
                      ref={ratingsRef}
                      id="product-section-rr"
                      className="accordion-placeholder"
                    >
                      <Placeholder
                        width="100%"
                        height="82px"
                        color="#F5F5F5"
                      />
                    </div>
                  </>
                )}
              >
                <ZoneCard className="zone-card">
                  <div
                    id="product-section-overview"
                    className="product-section-overview"
                    ref={productOverviewRef}
                  >
                    <Accordion
                      icon={{ size: 'sm' }}
                      iconPosition="right"
                      key="product-details-accordion"
                      className="collapsible-section"
                      expanded={expanded['#product-section-overview'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-overview')}
                    >
                      <AccordionTitle className="accordion-title-container">
                        <ProductOverviewAccordionTitle />
                      </AccordionTitle>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-product-overview"
                            className="grid"
                            waitFor={ProductOverviewAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="2000px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <Row>
                              <ErrorBoundary name="product-overview">
                                <ProductOverviewAccordionBody
                                  itemId={itemId}
                                  showReturnMessage
                                  revJet
                                  qctx={qctx}
                                />
                              </ErrorBoundary>
                            </Row>
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div
                    id="product-section-key-feat"
                    className="product-section-key-feat"
                    ref={specificationsRef}
                  >
                    <Accordion
                      icon={{ size: 'sm' }}
                      iconPosition="right"
                      key="specifications-accordion"
                      className="collapsible-section"
                      expanded={expanded['#product-section-key-feat'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-key-feat')}
                    >
                      <AccordionTitle className="accordion-title-container">
                        <SpecificationsAccordionTitle itemId={itemId} />
                      </AccordionTitle>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-specifications"
                            className="grid"
                            waitFor={[SpecificationsAccordionBodyLoad]}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="700px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <SpecificationsAccordionBody
                              itemId={itemId}
                              qctx={qctx}
                              returnableStatus
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div
                    id="product-section-qa"
                    className="product-section-qa"
                    ref={questionsRef}
                  >
                    <Accordion
                      key="questions-answers-accordion"
                      icon={{ size: 'sm' }}
                      iconPosition="right"
                      className="collapsible-section"
                      expanded={expanded['#product-section-qa'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-qa')}
                    >
                      <AccordionTitle className="accordion-title-container">
                        <QuestionsAndAnswersAccordionTitle
                          itemId={itemId}
                          layoutGroup={{
                            tag: Row,
                          }}
                          hydrator={{
                            className: 'grid'
                          }}
                          dynamic={{
                            pageType: 'pip'
                          }}
                        />
                      </AccordionTitle>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-questions-answers"
                            className="grid"
                            waitFor={QuestionsAndAnswersAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="700px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <QuestionsAndAnswersAccordionBody
                              itemId={itemId}
                              seoPageNumber={1}
                              layoutGroup={{
                                tag: Row,
                              }}
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div
                    id="product-section-rr"
                    className="product-section-rr"
                    ref={ratingsRef}
                  >
                    <Accordion
                      key="ratings-reviews-accordion"
                      icon={{ size: 'sm' }}
                      iconPosition="right"
                      className="collapsible-section collapsible-section--bottom-border"
                      expanded={expanded['#product-section-rr'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-rr')}
                    >
                      <AccordionTitle className="accordion-title-container">
                        <RatingsAndReviewsAccordionTitle
                          itemId={itemId}
                          layoutGroup={{
                            tag: Row,
                          }}
                          hydrator={{
                            className: 'grid'
                          }}
                          dynamic={{
                            pageType: 'pip'
                          }}
                        />
                      </AccordionTitle>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-ratings-reviews"
                            className="grid"
                            waitFor={RatingsAndReviewsAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="2000px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <RatingsAndReviewsAccordionBody
                              itemId={itemId}
                              qctx={qctx}
                              prosAndCons
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                </ZoneCard>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>
          <SponsoredPipBanner
            pageContext={{
              label: 'pip',
              data: {
                itemId,
                treatment: 'A'
              },
            }}
            errorBoundary
            name="pip-sponsored-banner-A"
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              id: 'hydrated-sponsored-pip-banner-A'
            }}
          />
          {/* <EducationalSummary
            itemId={itemId}
            layoutGroup={{
              tag: Row,
            }}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'grid'
            }}
          /> */}

          <ErrorBoundary name="sponsored-carousel-nucleus">
            <RetailMediaCarousel
              breadCrumbs={productData?.product?.taxonomy?.breadCrumbs}
              pageContext={{
                schema: 'pip_sponsored',
                data: { itemId },
                isPip: true
              }}
              showSponsoredCarousel
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          </ErrorBoundary>
          {fsSupplementalRecs && (
            <SupplementalRecommendations
              schemaName="ctl"
              anchorId={itemId}
              requestKey={requestKey}
              errorBoundary
              isDrawerShow
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'ctl',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}

          <QueryProvider
            defaultVariables={defaultVariables?.current}
            cacheKey="rv-pip-recs"
            persist
          >
            <RecentlyViewedWrapper
              schemaName="rv_gm_pip_rr"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'rv_gm_pip_rr',
                scrollBuffer: 600,
                delay: 2000
              }}
            />
          </QueryProvider>
          <FeatureBasedRecommendationsImporter
            hideOnMobile
            itemId={itemId}
            qctx={qctx}
            layoutGroup={{
              tag: Row,
            }}
            hydrator={{
              tag: ZoneCard,
              className: 'grid'
            }}
            dynamic={{
              pageType: 'pip'
            }}
          />

          <HdhomeSignup
            formSubmission
            subscriptionType={subscriptionType}
            campaignId={pipCampaignId}
            dynamic={{
              pageType: 'pip',
            }}
            hydrator={{
              className: 'grid',
              tag: ZoneCard
            }}
          />

          <RelatedSearch
            itemId={itemId}
            layoutGroup={{
              tag: Row,
            }}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'col__12-12'
            }}
          />
          <EmtLinks
            path={pathname}
            layoutGroup={{
              tag: Row
            }}
            dynamic={{
              pageType: 'pip',
            }}
            hydrator={{
              className: 'grid'
            }}
          />
        </Row>
      </ErrorBoundary>
    </>
  );
};

PaintReorder.displayName = 'PaintReorderPage';

PaintReorder.propTypes = {};

PaintReorder.dataModel = extend(
  {
    product: params({ itemId: string().isRequired })
      .shape({
        itemId: string(),
        dataSources: string(),
        seoDescription: string(),
        info: shape({
          recommendationFlags: shape({
            packages: bool(),
            pipCollections: bool(),
          }),
        }),
        identifiers: shape({
          upc: string()
        }),
        availabilityType: shape({
          discontinued: bool(),
          type: string()
        }),
        fulfillment: params({ storeId: string() })
          .shape({
            backordered: bool(),
            fulfillmentOptions: arrayOf(shape({
              type: string(),
              fulfillable: bool(),
              services: arrayOf(shape({
                type: string(),
                locations: arrayOf(shape({
                  isAnchor: bool(),
                  inventory: shape({
                    isLimitedQuantity: bool(),
                    isOutOfStock: bool()
                  })
                }))
              }))
            }))
          }),
        paintDetails: params({ storeId: string().isRequired(), configId: string().isRequired() }).shape({
          brandId: number(),
          colorDisplayName: string(),
          brandLogo: string(),
          rgb: shape({
            red: string(),
            green: string(),
            blue: string(),
            __typename: string()
          })
        }).skip('skipPaintDetails', false)
      })
  },
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
      itemId: string(),
      fulfillment: params({ storeId: string() }).shape({
        anchorStoreStatusType: string(),
        backordered: bool(),
        backorderedShipDate: string(),
        bossExcludedShipStates: string(),
        bodfsAssemblyEligible: bool(),
        seasonStatusEligible: bool(),
        excludedShipStates: string(),
        fulfillmentOptions: shape({
          fulfillable: bool(),
          messages: string(),
          services: shape({
            type: string(),
            isDefault: bool(),
            deliveryCharge: string(),
            deliveryDates: shape({
              endDate: string(),
              startDate: string(),
            }),
            deliveryTimeline: string(),
            freeDeliveryThreshold: string(),
            totalCharge: string(),
            locations: shape({
              type: string(),
              isAnchor: bool(),
              locationId: string(),
              storeName: string(),
              storePhone: string(),
              state: string(),
              distance: string(),
              inventory: shape({
                quantity: string(),
                isInStock: bool(),
                isOutOfStock: bool(),
                isLimitedQuantity: bool(),
                isUnavailable: bool(),
                maxAllowedBopisQty: string(),
                minAllowedBopisQty: string(),
              }),
            })
          }),
          type: string()
        }),
      }),
    })
  },
  PaintPIPRedirector,
  ProductMetadata,
  StickyNav,
  Sharebelt,
  HdhomeSignup,
  Discontinued,
  MediaGalleryWithHotspotsDesktop,
  AisleBay,
  ProductDetails,
  SalientPoints,
  Price,
  Promotion,
  ConsumerCardMessaging,
  OrderSample,
  SuperSku,
  ProductOverviewDesktop,
  PromotionProductsAnchor,
  FeatureBasedRecommendationsImporter,
  QuestionsAndAnswersDesktop,
  RatingsAndReviewsDesktopImporter,
  EmtLinks,
  RelatedSearch,
  GlobalCustomConfigurator,
  RecentlyViewedWrapper,
  PaintReorderBuyBox,
  SupplementalRecommendations
);
